import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { ExternalLink, ShieldCheck, Anchor, Sparkles, ArrowUpRight } from 'lucide-react';
import PublicLegacyDisclosure from '../components/PublicLegacyDisclosure';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PILLAR_META = {
  relocation: { label: 'Relocation', route: '/relocation', desc: 'Visa, immigration counsel, milestone tracker' },
  'real-estate': { label: 'Real Estate', route: '/real-estate', desc: 'Property search, brokers, sovereign listings' },
  finance: { label: 'Finance', route: '/finance', desc: 'Banking, currency armor, offshore accounts' },
  logistics: { label: 'Logistics', route: '/logistics', desc: 'Shipping, movers, sovereign mail address' },
};
const ALLOWED_PILLARS = Object.keys(PILLAR_META);

export default function FleetAffiliates() {
  const [catalog, setCatalog] = useState({ catalog: [] });

  useEffect(() => {
    axios.get(`${API}/rescue/sound-mastery`).then((r) => setCatalog(r.data)).catch(() => {});
  }, []);

  const onAffiliateClick = async (product) => {
    try {
      const { data } = await axios.post(`${API}/rescue/affiliate-click`, {
        product_id: product.id,
        referrer: typeof document !== 'undefined' ? document.referrer : null,
      });
      window.open(data.cta_url || product.cta_url, '_blank', 'noopener,noreferrer');
    } catch {
      window.open(product.cta_url, '_blank', 'noopener,noreferrer');
    }
  };

  // Group strictly by the 4 pillars; any non-pillar items are filtered out.
  const groups = (catalog.catalog || []).reduce((acc, p) => {
    if (!ALLOWED_PILLARS.includes(p.pillar)) return acc;
    (acc[p.pillar] = acc[p.pillar] || []).push(p);
    return acc;
  }, {});

  return (
    <div className="px-6 md:px-12 lg:px-24 py-16">
      {/* Hero */}
      <section className="relative mb-20">
        <div className="text-[10px] tracking-[0.4em] uppercase text-[#D4AF37] mb-6 flex items-center gap-3">
          <Sparkles size={12} strokeWidth={1.5} /> EIGOO Fleet · Vetted Partners
        </div>
        <h1 data-testid="affiliates-headline" className="text-5xl md:text-6xl lg:text-7xl font-extralight leading-[1] tracking-tight max-w-5xl">
          <span className="font-display italic text-[#D4AF37]">Our Affiliates.</span>
        </h1>
        <p className="text-lg text-[#A3A3A3] mt-8 max-w-3xl leading-relaxed font-light">
          A curated index of partners for the four pillars of your sovereign exit —
          <span className="text-[#F5F5F5]"> Relocation, Real Estate, Finance, and Logistics</span>.
          Every link is field-tested by EIGOO operators and tracked as an affiliate signal.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-12 max-w-3xl">
          <div className="border border-[#D4AF37]/30 bg-[#0A0A0A] p-5 flex items-start gap-3">
            <ShieldCheck size={18} strokeWidth={1.25} className="text-[#D4AF37] flex-shrink-0 mt-0.5" />
            <div>
              <div className="text-[10px] tracking-[0.3em] uppercase text-[#A3A3A3] mb-1">Vetted</div>
              <div className="text-xs text-[#F5F5F5] font-light leading-relaxed">Every partner field-tested by EIGOO operators.</div>
            </div>
          </div>
          <div className="border border-[#D4AF37]/30 bg-[#0A0A0A] p-5 flex items-start gap-3">
            <Anchor size={18} strokeWidth={1.25} className="text-[#D4AF37] flex-shrink-0 mt-0.5" />
            <div>
              <div className="text-[10px] tracking-[0.3em] uppercase text-[#A3A3A3] mb-1">Pillar-Aligned</div>
              <div className="text-xs text-[#F5F5F5] font-light leading-relaxed">Each link maps to one of the four sovereign pillars.</div>
            </div>
          </div>
          <div className="border border-[#D4AF37]/30 bg-[#0A0A0A] p-5 flex items-start gap-3">
            <Sparkles size={18} strokeWidth={1.25} className="text-[#D4AF37] flex-shrink-0 mt-0.5" />
            <div>
              <div className="text-[10px] tracking-[0.3em] uppercase text-[#A3A3A3] mb-1">Transparent</div>
              <div className="text-xs text-[#F5F5F5] font-light leading-relaxed">Every link is an affiliate link. Always disclosed.</div>
            </div>
          </div>
        </div>
      </section>

      {/* Pillar-grouped affiliate sections */}
      {ALLOWED_PILLARS.map((pid) => {
        const products = groups[pid] || [];
        const meta = PILLAR_META[pid];
        if (products.length === 0) return null;
        return (
          <section key={pid} data-testid={`affiliate-group-${pid}`} className="mb-20">
            <div className="flex items-baseline justify-between gap-4 flex-wrap mb-8">
              <div>
                <div className="text-[10px] tracking-[0.4em] uppercase text-[#D4AF37] mb-2">Pillar · {meta.label}</div>
                <h2 className="text-3xl md:text-4xl font-extralight tracking-tight">
                  The <span className="font-display italic text-[#D4AF37]">{meta.label}</span> stack.
                </h2>
                <p className="text-sm text-[#A3A3A3] font-light mt-2">{meta.desc}</p>
              </div>
              <Link to={meta.route} className="text-[10px] tracking-[0.3em] uppercase text-[#A3A3A3] hover:text-[#D4AF37] transition-colors flex items-center gap-2">
                Open {meta.label} Pillar <ArrowUpRight size={12} strokeWidth={1.5} />
              </Link>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {products.map((p) => (
                <div key={p.id} data-testid={`affiliate-card-${p.id}`} className="sov-border bg-[#0A0A0A] p-6 flex flex-col hover:bg-[#D4AF37]/5 transition-colors">
                  <div className="flex items-start justify-between gap-3 mb-3">
                    <div className="text-[10px] tracking-[0.3em] uppercase text-[#A3A3A3]">{p.category}</div>
                    <span className={`text-[9px] tracking-[0.25em] uppercase px-2 py-0.5 border ${p.tier === 'Master Tier' ? 'border-[#D4AF37] text-[#D4AF37]' : 'border-[#444] text-[#A3A3A3]'}`}>
                      {p.tier}
                    </span>
                  </div>
                  <div className="font-display text-xl text-[#F5F5F5] mb-1">{p.title}</div>
                  <div className="text-[10px] tracking-[0.3em] uppercase text-[#A3A3A3] mb-3">{p.vendor}</div>
                  <p className="text-sm text-[#A3A3A3] font-light leading-relaxed mb-5 flex-1">{p.blurb}</p>
                  {p.price_usd > 0 && (
                    <div className="text-[10px] tracking-[0.3em] uppercase text-[#666] mb-3">From ${p.price_usd}</div>
                  )}
                  <button
                    onClick={() => onAffiliateClick(p)}
                    data-testid={`affiliate-cta-${p.id}`}
                    className="mt-auto py-3 border border-[#D4AF37] text-[#D4AF37] text-[10px] tracking-[0.3em] uppercase hover:bg-[#D4AF37] hover:text-[#050505] transition-colors flex items-center justify-center gap-2"
                  >
                    <ExternalLink size={11} strokeWidth={1.5} /> Visit Partner
                  </button>
                </div>
              ))}
            </div>
          </section>
        );
      })}

      {/* Public Legacy Disclosure — single line, no internal split */}
      <div className="mt-12">
        <PublicLegacyDisclosure />
      </div>

      {/* Affiliate disclosure */}
      <section className="sov-border bg-[#0A0A0A] p-10 md:p-14 text-center mt-8">
        <div className="text-[10px] tracking-[0.4em] uppercase text-[#D4AF37] mb-4">Affiliate Disclosure</div>
        <p className="text-sm text-[#A3A3A3] max-w-2xl mx-auto font-light">
          When you purchase through an EIGOO Fleet Affiliate link, we may receive a referral
          commission at no additional cost to you. This funds our continued operations and
          allows us to keep <span className="text-[#F5F5F5]">LIVE FREE</span> tools accessible.
        </p>
        <div className="mt-8 text-[10px] tracking-[0.4em] uppercase text-[#D4AF37] flex items-center justify-center gap-3">
          <span className="w-6 h-px bg-[#D4AF37]" />
          Empowered by EIGOO INC
          <span className="w-6 h-px bg-[#D4AF37]" />
        </div>
      </section>
    </div>
  );
}
